<script setup>
import { computed, ref } from 'vue';
import CustomLink from './CustomLink.vue'

// Define component props with defaults
const props = defineProps({
  // Button styling
  variant: {
    type: String,
    default: 'gradient',
    validator: (value) => ['gradient', 'white', 'glass', 'glass-light'].includes(value)
  },
  darkVariant: {
    type: String,
    default: null,
    validator: (value) => !value || ['gradient', 'white', 'glass', 'glass-light'].includes(value)
  },
  size: {
    type: String,
    default: 'medium',
    validator: (value) => ['small', 'medium', 'large'].includes(value)
  },
  block: {
    type: Boolean,
    default: false
  },
  // Button functionality
  href: {
    type: String,
    default: null
  },
  type: {
    type: String,
    default: 'button'
  },
  disabled: {
    type: Boolean,
    default: false
  },
  loading: {
    type: Boolean,
    default: false
  },
  // Icon options
  icon: {
    type: String,
    default: null
  },
  iconPosition: {
    type: String,
    default: 'right',
    validator: (value) => ['left', 'right'].includes(value)
  },
  // Optional classes
  class: {
    type: String,
    default: ''
  }
});

// Compute classes based on props and dark mode
const buttonClasses = computed(() => {
  // Determine which variant to use based on dark mode
  const isDarkMode = document.documentElement.classList.contains('dark');
  const activeVariant = isDarkMode && props.darkVariant ? props.darkVariant : props.variant;
  
  return [
    'btn', // Base button class
    `btn-${activeVariant}`, // Variant (gradient, white, glass, glass-light)
    props.block ? 'w-full justify-center whitespace-nowrap px-6' : '',
    // Size classes
    props.size === 'small' ? 'text-sm px-3 py-1.5' : '',
    props.size === 'large' ? 'text-lg px-6 py-3' : '',
    // State classes
    props.disabled ? 'opacity-70 cursor-not-allowed' : '',
    props.loading ? 'opacity-90 cursor-wait' : '',
    // Custom classes
    props.class
  ].filter(Boolean).join(' ');
});

// Define events that can be emitted
const emit = defineEmits(['click']);

// Handle click and only emit if not disabled or loading
const handleClick = (event) => {
  if (!props.disabled && !props.loading) {
    emit('click', event);
  }
};

// Add mouse position tracking for the radial effect
const mouseX = ref('50%');
const mouseY = ref('50%');

// Function to update the mouse position within the button
const updateMousePosition = (event) => {
  const rect = event.currentTarget.getBoundingClientRect();
  mouseX.value = `${((event.clientX - rect.left) / rect.width) * 100}%`;
  mouseY.value = `${((event.clientY - rect.top) / rect.height) * 100}%`;
};
</script>

<template>
  <!-- Add responsive wrapper div when block=true -->
  <div v-if="block" class="w-full sm:w-auto">
    <!-- Render as link if href is provided -->
    <CustomLink v-if="href" :href="href" :class="buttonClasses" @mousemove="updateMousePosition" :style="{ '--mouse-x': mouseX, '--mouse-y': mouseY }" v-bind="$attrs">
      <div class="inline-flex items-center">
        <font-awesome-icon v-if="icon && iconPosition === 'left'" :icon="['fas', icon]" class="h-4 w-4 mr-1.5 transition-transform group-hover:translate-x-0.5" />
        <span class="text-nowrap"><slot /></span>
        <font-awesome-icon v-if="icon && iconPosition === 'right'" :icon="['fas', icon]" class="h-4 w-4 ml-1.5 transition-transform group-hover:translate-x-0.5" />
      </div>
      <font-awesome-icon v-if="loading" :icon="['fas', 'spinner']" class="h-4 w-4 ml-1.5 fa-spin" />
    </CustomLink>

    <!-- Render as button if no href -->
    <button v-else :type="type" :class="buttonClasses" :disabled="disabled || loading" @mousemove="updateMousePosition" :style="{ '--mouse-x': mouseX, '--mouse-y': mouseY }" @click="handleClick" v-bind="$attrs">
      <div class="inline-flex items-center">
        <font-awesome-icon v-if="icon && iconPosition === 'left'" :icon="['fas', icon]" class="h-4 w-4 mr-1.5 transition-transform group-hover:translate-x-0.5" />
        <span class="text-nowrap"><slot /></span>
        <font-awesome-icon v-if="icon && iconPosition === 'right'" :icon="['fas', icon]" class="h-4 w-4 ml-1.5 transition-transform group-hover:translate-x-0.5" />
      </div>
      <font-awesome-icon v-if="loading" :icon="['fas', 'spinner']" class="h-4 w-4 ml-1.5 fa-spin" />
    </button>
  </div>

  <!-- Same code without wrapper when block=false -->
  <template v-else>
    <CustomLink v-if="href" :href="href" :class="buttonClasses" @mousemove="updateMousePosition" :style="{ '--mouse-x': mouseX, '--mouse-y': mouseY }" v-bind="$attrs">
      <div class="inline-flex items-center">
        <font-awesome-icon v-if="icon && iconPosition === 'left'" :icon="['fas', icon]" class="h-4 w-4 mr-1.5 transition-transform group-hover:translate-x-0.5" />
        <span class="text-nowrap"><slot /></span>
        <font-awesome-icon v-if="icon && iconPosition === 'right'" :icon="['fas', icon]" class="h-4 w-4 ml-1.5 transition-transform group-hover:translate-x-0.5" />
      </div>
      <font-awesome-icon v-if="loading" :icon="['fas', 'spinner']" class="h-4 w-4 ml-1.5 fa-spin" />
    </CustomLink>

    <button v-else :type="type" :class="buttonClasses" :disabled="disabled || loading" @mousemove="updateMousePosition" :style="{ '--mouse-x': mouseX, '--mouse-y': mouseY }" @click="handleClick" v-bind="$attrs">
      <div class="inline-flex items-center">
        <font-awesome-icon v-if="icon && iconPosition === 'left'" :icon="['fas', icon]" class="h-4 w-4 mr-1.5 transition-transform group-hover:translate-x-0.5" />
        <span class="text-nowrap"><slot /></span>
        <font-awesome-icon v-if="icon && iconPosition === 'right'" :icon="['fas', icon]" class="h-4 w-4 ml-1.5 transition-transform group-hover:translate-x-0.5" />
      </div>
      <font-awesome-icon v-if="loading" :icon="['fas', 'spinner']" class="h-4 w-4 ml-1.5 fa-spin" />
    </button>
  </template>
</template>

<style>
/* Import Tailwind to make @apply work */
@import 'tailwindcss';

/* Base button class with shared animations and hover effects */
.btn { @apply relative inline-flex items-center justify-center rounded-full px-4 py-2 font-medium text-base shadow-md transition-all duration-500 overflow-hidden hover:shadow-lg hover:-translate-y-[2px]; }
.btn::before { content: ''; @apply absolute inset-0 opacity-0 z-[1]; transition: opacity 0.5s ease-in-out; }
.btn::after { 
  content: ''; 
  @apply absolute inset-0 opacity-0 z-[2]; 
  transition: opacity 0.3s ease-in-out; 
  background: radial-gradient(circle at var(--mouse-x) var(--mouse-y), rgba(255, 255, 255, 0.3) 0%, rgba(255, 255, 255, 0) 60%); 
}
.btn:hover::after { @apply opacity-90; }
/* Remove the hover::before style and just use the after pseudo-element for the radial effect */
.btn:hover::before { @apply opacity-100; }
.btn > * { @apply relative z-[3]; }

/* Gradient button style */
.btn-gradient { @apply text-white; background-image: linear-gradient(90deg, #D95F30 0%, #E68148 50%, #F1A36B 100%); background-size: 200% 200%; animation: gradient 4s ease infinite; }
.btn-gradient:hover { animation: gradient-wave 4s ease infinite; }
.btn-gradient::before { @apply bg-black/20; }
.btn-gradient::after { background: radial-gradient(circle at var(--mouse-x) var(--mouse-y), rgba(255, 255, 255, 0.15) 0%, rgba(255, 255, 255, 0) 60%); }

/* White button style */
.btn-white { @apply bg-white text-gray-700 border border-gray-300; }
.btn-white::before { @apply bg-black/5; }
.btn-white::after { background: radial-gradient(circle at var(--mouse-x) var(--mouse-y), rgba(200, 200, 200, 0.3) 0%, rgba(255, 255, 255, 0) 60%); }

/* Glass button style - updated to match PopoverGroup */
.btn-glass { @apply text-white bg-white/30 bg-clip-padding backdrop-filter backdrop-blur-md border border-white/30 shadow-sm; }
.btn-glass::before { @apply bg-white/5; }
.btn-glass::after { background: radial-gradient(circle at var(--mouse-x) var(--mouse-y), rgba(255, 255, 255, 0.4) 0%, rgba(255, 255, 255, 0) 70%); }

/* Glass light button style - for light backgrounds */
.btn-glass-light { @apply text-gray-700 bg-gray-100/50 bg-clip-padding backdrop-filter backdrop-blur-md border border-gray-300/50 shadow-sm; }
.btn-glass-light::before { @apply bg-gray-600/5; }
.btn-glass-light::after { background: radial-gradient(circle at var(--mouse-x) var(--mouse-y), rgba(255, 255, 255, 0.6) 0%, rgba(255, 255, 255, 0) 70%); }





</style>@keyframes gradient { 0% { background-position: 0% 50%; } 50% { background-position: 100% 50%; } 100% { background-position: 0% 50%; } }@keyframes gradient-wave { 0% { background-position: 0% 50%; } 50% { background-position: 100% 50%; } 100% { background-position: 0% 50%; } }/* Animation keyframes needed for button animations */